import { useLazyQuery } from '@apollo/client/react';
import { Sparkles } from 'lucide-react';
import { toast } from 'sonner';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { AppSuggestionDocument, type IAppSuggestionQuery } from 'src/graphql/actions/apps/appSuggestion.query';
import { getApiError } from 'src/utils/errors';
import { cn } from 'src/lib/utils';

export type IAppSuggestion = IAppSuggestionQuery['appSuggestion'];

type AutofillPromptBannerProps = {
  appName: string;
  onEmptyName: () => void;
  onSuccess: (suggestion: IAppSuggestion) => void;
};

const AutofillPromptBanner = ({ appName, onEmptyName, onSuccess }: AutofillPromptBannerProps) => {
  const [fetchAppSuggestion, { loading: isLoading }] = useLazyQuery(AppSuggestionDocument, {
    fetchPolicy: 'network-only',
  });

  const handleClick = async () => {
    if (isLoading) return;

    const trimmedAppName = appName.trim();
    if (!trimmedAppName) {
      onEmptyName();
      return;
    }

    try {
      const { data, error } = await fetchAppSuggestion({ variables: { appName: trimmedAppName } });
      if (error || !data) throw error ?? new Error('No suggestion returned.');

      onSuccess(data.appSuggestion);
    } catch (error) {
      getApiError(error, toast.error);
    }
  };

  return (
    <Alert className="bg-mint-tint grid-cols-[38px_1fr_auto] border-accent-software-escrow items-start gap-4 p-4">
      <div className="bg-secondary-foreground flex items-center justify-center rounded-md size-9.5">
        <Sparkles className="size-4.5 text-white" />
      </div>
      <div className="col-start-2 space-y-1">
        <AlertTitle className="font-extrabold">Let Codekeeper fill this in</AlertTitle>
        <AlertDescription className="text-xs">
          Enter the application name, and we&apos;ll suggest its industry, type, and description. You can edit anything
          we suggest.
        </AlertDescription>
      </div>
      <div className="col-start-2 sm:col-start-3 sm:self-center">
        <Button variant="secondary" disabled={isLoading} onClick={handleClick}>
          <Sparkles className={cn(isLoading && 'animate-pulse')} />
          {isLoading ? 'Analyzing…' : 'Auto-fill details'}
        </Button>
      </div>
    </Alert>
  );
};

export default AutofillPromptBanner;
